import { useEffect, useState } from "react";
import { getMeta } from "@veto/storage";
import { confirmAction } from "./ConfirmDialog";
import type { Contact, Conversation, DisappearTimerSec, GroupRecord } from "@veto/domain";
import { DISAPPEAR_OPTIONS } from "@veto/domain";
import { Avatar } from "@veto/ui";
import { ContactVerify } from "./ContactVerify";
import { useProfileMediaUrl } from "../profile/profileMedia";

function formatSafety(sn: string): string {
  return sn.replace(/(.{5})/g, "$1 ").trim();
}

export function ContactDetailsPane({
  contact,
  conversation,
  disappearTimer,
  onVerify,
  onTogglePin,
  onToggleMute,
  onArchive,
  onBlock,
  onDisappearChange,
  onToggleChatLock,
  onClearMessages,
  onDeleteChat,
  onShowStarred,
  onReport,
}: {
  onReport?: () => void;
  contact: Contact;
  conversation: Conversation | null;
  disappearTimer: DisappearTimerSec;
  /** One-tap attest: caller already compared out-of-band (FR-CON-002). */
  onVerify: () => void | Promise<void>;
  onTogglePin: () => void;
  onToggleMute: () => void;
  onArchive: () => void;
  onBlock: () => void;
  onDisappearChange: (sec: DisappearTimerSec) => void;
  /** Enables the local PIN lock (opens modal) or removes an existing one. */
  onToggleChatLock: () => void;
  onClearMessages: () => void;
  onDeleteChat: () => void;
  onShowStarred?: () => void;
}) {
  // FR-CON-002/003 without number-typing friction: TOFU lets you message immediately.
  // Verification lives in <ContactVerify> (scan/show QR + out-of-band attestation).
  const avatarUrl = useProfileMediaUrl(contact.avatar ?? null);
  return (
    <div className="details details-sheet">
      <div className="profile">
        <Avatar
          name={contact.displayName}
          size={88}
          verified={contact.verificationState === "verified"}
          media={avatarUrl && contact.avatar ? { url: avatarUrl, kind: contact.avatar.kind } : null}
        />
        <strong className="profile-name">{contact.displayName}</strong>
        {contact.about ? <p className="profile-about">{contact.about}</p> : null}
        {contact.verificationState === "verified" ? (
          <span className="verify-chip ok">Verified</span>
        ) : contact.verificationState === "changed" ? (
          <span className="verify-chip danger">Keys changed</span>
        ) : (
          <span className="verify-chip improve">Improve safety</span>
        )}
        {contact.blockedAt ? <span className="verify-chip danger">Blocked</span> : null}
      </div>
      <div>
        <div className="details-section-label">Safety number</div>
        <div className="safety">{formatSafety(contact.safetyNumber)}</div>
      </div>
      {contact.verificationState !== "verified" && (
        <ContactVerify contact={contact} onVerify={onVerify} />
      )}
      <div className="actions-row">
        <button className="btn secondary small" type="button" onClick={onTogglePin}>
          {conversation?.pinned ? "Unpin chat" : "Pin chat"}
        </button>
        <button className="btn secondary small" type="button" onClick={onToggleMute}>
          {conversation?.muted ? "Unmute notifications" : "Mute notifications"}
        </button>
        <button className="btn secondary small" type="button" onClick={onArchive}>
          {conversation?.archived ? "Unarchive" : "Archive"}
        </button>
        <button className="btn secondary small" type="button" onClick={onToggleChatLock}>
          <LockLabel conversationId={conversation?.id} />
        </button>
        {onShowStarred && (
          <button className="btn secondary small" type="button" onClick={onShowStarred}>
            Starred messages
          </button>
        )}
        <button className="btn secondary small" type="button" onClick={onClearMessages}>
          Clear chat
        </button>
        <button className="btn danger small" type="button" onClick={onBlock}>
          {contact.blockedAt ? "Unblock" : "Block"}
        </button>
        {onReport && !contact.blockedAt && (
          <button className="btn danger small" type="button" onClick={onReport}>
            Report &amp; block
          </button>
        )}
        <button className="btn danger small" type="button" onClick={onDeleteChat}>
          Delete chat
        </button>
      </div>
      <div className="field">
        <label htmlFor="disappearTimer">Disappearing messages</label>
        <select
          id="disappearTimer"
          value={disappearTimer}
          onChange={(e) => onDisappearChange(Number(e.target.value) as DisappearTimerSec)}
        >
          {DISAPPEAR_OPTIONS.map((o) => (
            <option key={o.sec} value={o.sec}>
              {o.label}
            </option>
          ))}
        </select>
      </div>
      <p className="muted text-sm disappear-copy" style={{ margin: 0 }}>
        Timer removes the message from this device after the delay. Recipients may still retain
        copies — disappearing is not a remote wipe. Public mesh stays disabled.
      </p>
    </div>
  );
}

/** Group member avatar — resolves received profile media from the local contact record. */
function MemberAvatar({
  contacts,
  contactId,
  displayName,
}: {
  contacts: Contact[];
  contactId: string;
  displayName: string;
}) {
  const contact = contacts.find((c) => c.id === contactId);
  const url = useProfileMediaUrl(contact?.avatar ?? null);
  return (
    <Avatar
      name={displayName}
      size={30}
      media={url && contact?.avatar ? { url, kind: contact.avatar.kind } : null}
    />
  );
}

/** Chat-level controls offered on the group details sheet (parity with 1:1 chats). */
export type GroupChatControls = {
  conversation: Conversation;
  disappearTimer: DisappearTimerSec;
  onTogglePin: () => void;
  onToggleMute: () => void;
  onArchive: () => void;
  onDisappearChange: (sec: DisappearTimerSec) => void;
  onToggleChatLock: () => void;
  onClearMessages: () => void;
  onDeleteChat: () => void;
  onShowStarred?: () => void;
};

export function GroupDetailsPane({
  group,
  forkWarning,
  selfIsAdmin = false,
  contacts = [],
  onRemove,
  onSetRole,
  onAddMembers,
  onRename,
  chatControls,
}: {
  group: GroupRecord;
  /** Admins: rename the group (new epoch, signed like any change). */
  onRename?: (title: string) => void;
  /** FR-GRP-005: visible when membership/epoch fork detected */
  forkWarning?: string | null;
  /** Only admins see membership mutation controls (FR-GRP-001). */
  selfIsAdmin?: boolean;
  /** Contacts eligible to add (not already members). */
  contacts?: Contact[];
  onRemove: (identityKeyHex: string) => void;
  onSetRole?: (identityKeyHex: string, role: "admin" | "member") => void;
  /** FR-GRP-003: add members without sharing prior history. */
  onAddMembers?: (contactIds: string[]) => void;
  chatControls?: GroupChatControls;
}) {
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [title, setTitle] = useState(group.title);
  useEffect(() => setTitle(group.title), [group.title]);
  const memberKeys = new Set(group.members.map((m) => m.identityKeyHex.toLowerCase()));
  const candidates = contacts.filter(
    (c) => !c.blockedAt && !memberKeys.has(c.messagingPublicKeyHex.toLowerCase()),
  );

  function togglePick(id: string) {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    <div className="details details-sheet">
      <h3 className="profile-name" style={{ margin: 0 }}>
        {group.title}
      </h3>
      <p className="muted" style={{ margin: 0 }}>
        Group · {group.members.length === 1 ? "1 member" : `${group.members.length} members`}
      </p>
      {selfIsAdmin && onRename && (
        <form
          className="group-rename"
          onSubmit={(e) => {
            e.preventDefault();
            if (title.trim() && title.trim() !== group.title) onRename(title.trim());
          }}
        >
          <label htmlFor="groupTitle" className="details-section-label">
            Group name
          </label>
          <div className="actions-row">
            <input
              id="groupTitle"
              value={title}
              maxLength={64}
              onChange={(e) => setTitle(e.target.value)}
            />
            <button
              type="submit"
              className="btn secondary small"
              disabled={!title.trim() || title.trim() === group.title}
            >
              Rename
            </button>
          </div>
        </form>
      )}
      {forkWarning && (
        <div className="assurance" role="alert">
          Membership fork detected: {forkWarning}. Compare members out of band; do not assume a
          single shared group history.
        </div>
      )}
      {chatControls && (
        <div className="group-chat-controls">
          <div className="actions-row">
            <button
              className="btn secondary small"
              type="button"
              onClick={chatControls.onTogglePin}
            >
              {chatControls.conversation.pinned ? "Unpin chat" : "Pin chat"}
            </button>
            <button
              className="btn secondary small"
              type="button"
              onClick={chatControls.onToggleMute}
            >
              {chatControls.conversation.muted ? "Unmute notifications" : "Mute notifications"}
            </button>
            <button className="btn secondary small" type="button" onClick={chatControls.onArchive}>
              {chatControls.conversation.archived ? "Unarchive" : "Archive"}
            </button>
            <button
              className="btn secondary small"
              type="button"
              onClick={chatControls.onToggleChatLock}
            >
              <LockLabel conversationId={chatControls.conversation.id} />
            </button>
            {chatControls.onShowStarred && (
              <button
                className="btn secondary small"
                type="button"
                onClick={chatControls.onShowStarred}
              >
                Starred messages
              </button>
            )}
            <button
              className="btn secondary small"
              type="button"
              onClick={chatControls.onClearMessages}
            >
              Clear chat
            </button>
            <button className="btn danger small" type="button" onClick={chatControls.onDeleteChat}>
              Leave group
            </button>
          </div>
        </div>
      )}
      <p className="details-section-label">
        Members · {group.members.length}
        {selfIsAdmin ? " · you can manage roles" : ""}
      </p>
      {group.members.map((m) => (
        <div key={m.identityKeyHex} className="device-card member-card">
          <div className="member-head">
            <div className="member-identity">
              <MemberAvatar
                contacts={contacts}
                contactId={m.contactId}
                displayName={m.displayName}
              />
              <strong>
                {m.displayName}
                {m.contactId === "self" ? (
                  <span className="member-you" aria-label="You">
                    {" "}
                    (you)
                  </span>
                ) : null}
              </strong>
              <span
                className={`role-chip role-${m.role}`}
                aria-label={m.role === "admin" ? "Admin" : "Member"}
                title={
                  m.role === "admin"
                    ? "Admin — can add/remove members and change roles (no special decryption)"
                    : "Member"
                }
              >
                {m.role === "admin" ? "Admin" : "Member"}
              </span>
            </div>
            {selfIsAdmin && m.role !== "admin" && m.contactId !== "self" && (
              <button
                type="button"
                className="btn danger small"
                onClick={() => {
                  void confirmAction({
                    title: `Remove ${m.displayName}?`,
                    body: "They stop receiving new messages in this group.",
                    action: "Remove",
                  }).then((ok) => ok && onRemove(m.identityKeyHex));
                }}
                title="Remove from the group"
              >
                Remove
              </button>
            )}
          </div>
          {selfIsAdmin && onSetRole && m.contactId !== "self" && (
            <div className="actions-row member-admin-actions">
              {m.role !== "admin" ? (
                <button
                  type="button"
                  className="btn secondary small"
                  onClick={() => onSetRole(m.identityKeyHex, "admin")}
                >
                  Make admin
                </button>
              ) : (
                <button
                  type="button"
                  className="btn secondary small"
                  onClick={() => onSetRole(m.identityKeyHex, "member")}
                >
                  Demote
                </button>
              )}
            </div>
          )}
        </div>
      ))}
      {group.members.length === 0 && (
        <p className="muted text-sm" role="status">
          No members in this epoch — leave and rejoin, or create a new group.
        </p>
      )}
      {selfIsAdmin && onAddMembers && candidates.length > 0 && (
        <div className="device-card" style={{ marginTop: 12 }}>
          <strong>Add members</strong>
          <p className="muted text-xs" style={{ margin: "4px 0 8px" }}>
            New members don&apos;t receive prior history.
          </p>
          <div className="scroll-list" style={{ maxHeight: 160 }}>
            {candidates.map((c) => (
              <label key={c.id} className="check">
                <input
                  type="checkbox"
                  checked={picked.has(c.id)}
                  onChange={() => togglePick(c.id)}
                />
                <span>
                  <strong>{c.displayName}</strong>
                </span>
              </label>
            ))}
          </div>
          <button
            type="button"
            className="btn small"
            disabled={picked.size === 0}
            onClick={() => {
              onAddMembers([...picked]);
              setPicked(new Set());
            }}
          >
            Add selected
          </button>
        </div>
      )}
    </div>
  );
}

/** "Lock chat" / "Unlock chat", like the phone (the lock lives in the chat's meta). */
function LockLabel({ conversationId }: { conversationId?: string }) {
  const [locked, setLocked] = useState(false);
  useEffect(() => {
    let live = true;
    if (conversationId)
      void getMeta(`chatlock.${conversationId}`).then((v) => live && setLocked(!!v));
    return () => {
      live = false;
    };
  }, [conversationId]);
  return <>{locked ? "Unlock chat" : "Lock chat"}</>;
}
